import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  CircularProgress,
  Box,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { useTranslation } from "react-i18next";

interface PdfViewerProps {
  open: boolean;
  onClose: () => void;
  pdfUrl: string;
  fileName?: string;
}

const PdfViewer: React.FC<PdfViewerProps> = ({
  open,
  onClose,
  pdfUrl,
  fileName = "document.pdf",
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [blobUrl, setBlobUrl] = useState<string>("");
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (!open) {
      // Clean up blob URL when dialog closes
      if (blobUrl) {
        window.URL.revokeObjectURL(blobUrl);
        setBlobUrl("");
      }
      return;
    }

    // Fetch PDF as blob and create object URL
    const loadPdf = async () => {
      try {
        setIsLoading(true);
        setError("");
        const response = await fetch(pdfUrl);
        if (!response.ok) {
          throw new Error(`Failed to load PDF: ${response.statusText}`);
        }
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        setBlobUrl(url);
      } catch (err) {
        console.error(t("error.failedToLoadPdf"), err);
        setError(
          err instanceof Error ? err.message : t("error.failedToLoadPdf"),
        );
      } finally {
        setIsLoading(false);
      }
    };

    loadPdf();
  }, [open, pdfUrl]);

  const handleDownload = async () => {
    try {
      const response = await fetch(pdfUrl);
      if (!response.ok) {
        throw new Error(t("error.failedToDownloadPdf"));
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download PDF:", err);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: "90vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span>
          {t("common.viewPdf")} - {fileName}
        </span>
        <IconButton
          size="small"
          onClick={handleDownload}
          title={t("common.download")}
          sx={{ ml: 2 }}
        >
          <DownloadIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          overflow: "auto",
          backgroundColor: "#f5f5f5",
          p: 0,
        }}
      >
        {isLoading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              color: "error.main",
            }}
          >
            {error}
          </Box>
        )}
        {!isLoading && !error && blobUrl && (
          <iframe
            src={`${blobUrl}#toolbar=1&navpanes=0&scrollbar=1`}
            style={{
              width: "100%",
              height: "100%",
              border: "none",
              borderRadius: "4px",
            }}
            title={fileName}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: "1px solid #e0e0e0" }}>
        <Button onClick={onClose} variant="contained">
          {t("common.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PdfViewer;
